import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface SellerProfile {
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  phone?: string;
  status?: string;

  // Nested sellerProfile fields - make them optional at top level
  gstin?: string | null;
  panNumber?: string | null;
  businessAddress?: string | null;
  bankAccount?: string | null;
  ifscCode?: string | null;
  businessName?: string | null;
  businessType?: string | null;
  totalProducts?: number;
  totalOrders?: number;
  totalRevenue?: number;
  rating?: number;

  // Also keep the original nested structure for reference
  sellerProfile?: {
    gstin?: string | null;
    panNumber?: string | null;
    businessAddress?: string | null;
    bankAccount?: string | null;
    ifscCode?: string | null;
    businessName?: string | null;
    businessType?: string | null;
    totalProducts?: number;
    totalOrders?: number;
    totalRevenue?: number;
    rating?: number;
    status?: string;
  };

  [key: string]: any;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthContextType {
  seller: SellerProfile | null;
  isLoading: boolean;
  login: (profileData: any, tokens: AuthTokens) => void; // Use any for flexibility
  logout: () => void;
  updateProfile: (updates: Partial<SellerProfile>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [seller, setSeller] = useState<SellerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("sellerProfile");
    const token = localStorage.getItem("sellerAccessToken");
    if (stored && token) {
      try {
        setSeller(JSON.parse(stored));
      } catch (err) {
        console.error("Failed to parse stored seller profile", err);
      }
    }
    setIsLoading(false);
  }, []);

  const login = (profileData: any, tokens: AuthTokens) => {
    // Log the incoming data for debugging
    console.log("Login profile data:", profileData);

    // Flatten the profile structure
    const flattenedProfile: SellerProfile = {
      // Basic fields from root
      email: profileData.email || "",
      firstName: profileData.firstName || "",
      lastName: profileData.lastName || "",
      emailVerified: profileData.emailVerified || false,
      status:
        profileData.status ||
        profileData.sellerProfile?.status ||
        "PENDING_APPROVAL",

      // Extract fields from sellerProfile if they exist
      gstin: profileData.sellerProfile?.gstin || null,
      panNumber: profileData.sellerProfile?.panNumber || null,
      businessAddress: profileData.sellerProfile?.businessAddress || null,
      bankAccount: profileData.sellerProfile?.bankAccount || null,
      ifscCode: profileData.sellerProfile?.ifscCode || null,
      businessName: profileData.sellerProfile?.businessName || null,
      businessType: profileData.sellerProfile?.businessType || null,
      totalProducts: profileData.sellerProfile?.totalProducts || 0,
      totalOrders: profileData.sellerProfile?.totalOrders || 0,
      totalRevenue: profileData.sellerProfile?.totalRevenue || 0,
      rating: profileData.sellerProfile?.rating || 0,

      // Also store the original nested structure
      sellerProfile: profileData.sellerProfile || {},
    };

    console.log("Flattened profile to store:", flattenedProfile);

    // Store tokens
    localStorage.setItem("sellerAccessToken", tokens.accessToken);
    localStorage.setItem("sellerRefreshToken", tokens.refreshToken);

    // Store complete flattened profile
    localStorage.setItem("sellerProfile", JSON.stringify(flattenedProfile));

    // Store individual fields for easy access
    localStorage.setItem("sellerEmail", flattenedProfile.email);
    localStorage.setItem("sellerName", flattenedProfile.firstName);
    localStorage.setItem(
      "sellerStatus",
      flattenedProfile.status || "PENDING_APPROVAL",
    );

    // Store GST and PAN if they exist
    if (flattenedProfile.gstin) {
      localStorage.setItem("sellerGstin", flattenedProfile.gstin);
    }
    if (flattenedProfile.panNumber) {
      localStorage.setItem("sellerPanNumber", flattenedProfile.panNumber);
    }
    if (flattenedProfile.businessAddress) {
      localStorage.setItem(
        "sellerBusinessAddress",
        flattenedProfile.businessAddress,
      );
    }

    setSeller(flattenedProfile);
  };

  const logout = () => {
    // Remove all seller-related items from localStorage
    localStorage.removeItem("sellerAccessToken");
    localStorage.removeItem("sellerRefreshToken");
    localStorage.removeItem("sellerProfile");
    localStorage.removeItem("sellerEmail");
    localStorage.removeItem("sellerName");
    localStorage.removeItem("sellerGstin");
    localStorage.removeItem("sellerPanNumber");
    localStorage.removeItem("sellerBusinessAddress");
    localStorage.removeItem("sellerStatus");

    setSeller(null);
  };

  const updateProfile = (updates: Partial<SellerProfile>) => {
    if (seller) {
      const updated = { ...seller, ...updates };
      localStorage.setItem("sellerProfile", JSON.stringify(updated));

      // Update individual fields if they exist in updates
      if (updates.gstin !== undefined) {
        localStorage.setItem("sellerGstin", updates.gstin || "");
      }
      if (updates.panNumber !== undefined) {
        localStorage.setItem("sellerPanNumber", updates.panNumber || "");
      }
      if (updates.businessAddress !== undefined) {
        localStorage.setItem(
          "sellerBusinessAddress",
          updates.businessAddress || "",
        );
      }
      if (updates.firstName !== undefined) {
        localStorage.setItem("sellerName", updates.firstName);
      }
      if (updates.status !== undefined) {
        localStorage.setItem("sellerStatus", updates.status);
      }

      setSeller(updated);
    }
  };

  return (
    <AuthContext.Provider
      value={{ seller, isLoading, login, logout, updateProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
